<template>
    <section
        :data-mui-theme="theme"
        :class="['slice slice-hero slice-hero--with-search', {'slice-hero--hide-in-mobile': slice.primary.hideInMobile}]"
        aria-label="hero"
        data-testid="slice-hero-with-search"
    >
        <div class="slice-content text-left">
            <div class="slice-hero__content-wrapper">
                <div class="slice-hero--with-search__main mui-background-lighter">
                    <div class="slice-hero--with-search__shapes" aria-hidden="true">
                        <svg width="822" height="261" viewBox="0 0 822 261" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M1344.01 473.258C1326.15 577.613 1232.88 584.266 1163.12 572.326L833.572 515.938L504.036 459.549C434.272 447.616 347.577 415.822 366.373 305.974C385.168 196.127 477.501 194.967 547.265 206.904L876.806 263.291L1206.34 319.679C1276.11 331.615 1361.86 368.901 1344.01 473.258Z"
                                fill="#FF91F0"
                            />
                            <path
                                d="M1314.42 845.012C1254.02 976.754 1128.3 952.675 1040.21 912.294L624.18 721.539L208.144 530.784C120.068 490.402 16.597 417.787 80.1826 279.111C143.767 140.434 266.314 171.447 354.39 211.83L770.425 402.584L1186.47 593.332C1274.54 633.721 1374.83 713.27 1314.42 845.012Z"
                                fill="#FC5757"
                            />
                            <path
                                d="M923.681 1167.65C806.542 1293.28 674.108 1219.27 590.113 1140.97L193.354 771.092L-203.406 401.226C-287.401 322.925 -376.678 202.619 -253.377 70.3684C-130.076 -61.8827 -3.80817 18.7481 80.1868 97.0499L476.947 466.915L873.703 836.789C957.705 915.093 1040.81 1042.01 923.681 1167.65Z"
                                fill="#FF7370"
                            />
                        </svg>
                    </div>
                    <div class="slice-hero--with-search__main-content">
                        <div class="slice-hero--with-search__title">
                            <h1 class="slice-hero--with-search__title-line" data-testid="slice-hero-with-search-title-line">
                                {{ titlePrefix }}
                                <span v-if="currentRotatingWord" data-testid="slice-hero-with-search-title-rotating-word">
                                    {{ currentRotatingWord }}
                                </span>
                            </h1>
                            <PrismicRichText
                                :field="slice.primary.titleSufix"
                                class="slice-hero--with-search__title-suffix"
                                data-testid="slice-hero-with-search-title-suffix"
                            />
                        </div>
                        <PrismicRichText
                            v-if="isFilled.richText(slice.primary.description)"
                            :field="slice.primary.description"
                            class="slice-hero--with-search__description"
                            data-testid="slice-hero-with-search-description"
                        />
                        <div class="slice-hero--with-search__panel" data-testid="slice-hero-search">
                            <SearchPanel
                                :search-tab="keywordSearchLinkText"
                                :search-field-category="slice.primary.categoryLabel"
                                :search-field-location="slice.primary.locationLabel"
                                :search-location-remote-country-label="slice.primary.remoteInCountryLabel"
                                :search-submit="keywordSearchLinkText"
                                :search-submit-link="keywordSearchLinkUrl"
                                :ai-search-tab="aiSearchLinkText"
                                :ai-search-tab-link="aiSearchLinkUrl"
                                brief-origin="home_unlogged"
                                page-source="home_unlogged_try_ai_search"
                                origin="USER_PROMPT_HOMEPAGE_UNLOGGED"
                            />
                        </div>

                        <div class="slice-hero--with-search__reinsurance">
                            <span v-for="(item, i) in slice.primary.items" :key="i" class="slice-hero--with-search__reinsurance-item">
                                <span v-if="hasRatingScale(item.reassuranceText)" class="slice-hero--with-search__reinsurance-stars">
                                    <VJoyIcon v-for="star in 5" :key="star" name="star-fill" color="quaternary" size="xsmall" />
                                </span>
                                <PrismicRichText v-if="isFilled.richText(item.reassuranceText)" :field="item.reassuranceText" />
                            </span>
                        </div>
                    </div>
                </div>
                <div class="slice-hero--with-search__image">
                    <PrismicImage :field="slice.primary.image" :widths="[536, 636]" loading="eager" />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, onMounted, onUnmounted, ref} from 'vue';
    import {asText, isFilled} from '@prismicio/helpers';
    import type {RichTextField} from '@prismicio/types';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {HeroSlice} from '@prismicio-types';
    import SearchPanel from '@search/components/SearchPanel.vue';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import useErrorHelpers from '../../../composables/useErrorHelpers';

    type HeroWithSearchInZone = Extract<HeroSlice, {variation: 'heroWithSearch'}>;
    const props = defineProps(getSliceComponentProps<HeroWithSearchInZone>());

    const theme = computed(() => props.slice.primary.theme);

    const searchLink = computed(() => {
        if (!isFilled.link(props.slice.primary.searchLink)) return undefined;
        return props.slice.primary.searchLink as {url?: string; text?: string};
    });

    const submitProjectLink = computed(() => {
        if (!isFilled.link(props.slice.primary.submitProject)) return undefined;
        return props.slice.primary.submitProject as {url?: string; text?: string};
    });

    const keywordSearchLinkText = computed(() => submitProjectLink.value?.text ?? undefined);
    const keywordSearchLinkUrl = computed(() => submitProjectLink.value?.url ?? undefined);
    const aiSearchLinkText = computed(() => searchLink.value?.text ?? undefined);
    const aiSearchLinkUrl = computed(() => searchLink.value?.url ?? undefined);

    function hasRatingScale(reassuranceText: RichTextField | undefined): boolean {
        return isFilled.richText(reassuranceText) && asText(reassuranceText).includes('/5');
    }

    const currentRotatingWordIndex = ref(0);
    const rotatingWordInterval = ref<ReturnType<typeof setInterval> | undefined>(undefined);

    const rotatingWords = computed(() => (props.slice.primary.titleRotatingWords ?? []).filter((item) => isFilled.richText(item.word)));

    const titlePrefix = computed(() => (isFilled.richText(props.slice.primary.titlePrefix) ? asText(props.slice.primary.titlePrefix) : ''));

    const currentRotatingWord = computed(() => {
        const word = rotatingWords.value[currentRotatingWordIndex.value]?.word;
        return isFilled.richText(word) ? asText(word) : '';
    });

    onMounted(() => {
        if (rotatingWords.value.length > 1) {
            rotatingWordInterval.value = setInterval(() => {
                currentRotatingWordIndex.value = (currentRotatingWordIndex.value + 1) % rotatingWords.value.length;
            }, 2000);
        }

        const {getPreviewErrorHandler, throwIfPrimaryPropertiesAreInvalid} = useErrorHelpers();
        const errorHandler = getPreviewErrorHandler();
        if (errorHandler.enabled) {
            throwIfPrimaryPropertiesAreInvalid(
                errorHandler.toast,
                props.slice.primary,
                [
                    {name: 'titlePrefix', type: 'field'},
                    {name: 'titleSufix', type: 'field'},
                    {name: 'description', type: 'field'},
                    {name: 'image', type: 'image'},
                ],
                'hero',
            );
        }
    });

    onUnmounted(() => {
        if (rotatingWordInterval.value) {
            clearInterval(rotatingWordInterval.value);
        }
    });
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-hero {
        &--with-search {
            .slice-content {
                padding: 0;

                @include mq.screen_lg(min) {
                    padding-top: var(--joy-core-spacing-8);
                }
            }

            &__description {
                color: var(--joy-color-neutral-70);
                margin-bottom: var(--joy-core-spacing-8);
                @include text.getFontBySize(primary-400);
                margin-top: var(--joy-core-spacing-3);
            }

            &__shapes {
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                pointer-events: none;
                z-index: 0;

                svg {
                    display: block;
                    width: 100%;
                    height: auto;
                }
            }

            &__title-line,
            &__title-suffix h1 {
                font-family: var(--joy-font-family-title);
                font-weight: var(--joy-font-weight-bold);
                font-size: 42px;
                line-height: 105%;
                margin: 0;
                color: var(--joy-color-neutral-70);

                span {
                    color: var(--mui-color-text);
                }

                @include mq.screen_sm(min) {
                    font-size: 64px;
                }
            }

            .slice-hero__content-wrapper {
                display: block;
                gap: var(--joy-core-spacing-4);

                @include mq.screen_lg(min) {
                    display: grid;
                    grid-template-columns: 2fr 1fr;
                    align-items: stretch;
                }
            }

            &__main {
                position: relative;
                overflow: hidden;
                width: 100%;
                padding: var(--joy-core-spacing-10) var(--joy-core-spacing-5) var(--joy-core-spacing-12) var(--joy-core-spacing-5);

                @include mq.screen_md(min) {
                    min-width: 0;
                    width: auto;
                    padding: 128px var(--joy-core-spacing-12);
                }

                @include mq.screen_lg(min) {
                    border-radius: var(--joy-core-radius-4);
                }
            }

            &__main-content {
                position: relative;
                z-index: 1;
            }

            &__image {
                display: none;

                @include mq.screen_lg(min) {
                    display: flex;
                    min-width: 0;
                    min-height: 0;
                    overflow: hidden;
                    border-radius: var(--joy-core-radius-4);

                    img {
                        width: 100%;
                        height: 100%;
                        border-radius: var(--joy-core-radius-4);
                        object-fit: cover;
                    }
                }
            }

            &__panel {
                width: 100%;
                margin-top: var(--joy-core-spacing-8);
            }

            &__reinsurance {
                @include text.getFontBySize(primary-300);
                display: flex;
                flex-wrap: wrap;
                gap: 10px;
                margin-top: var(--joy-core-spacing-5);

                &-item {
                    display: flex;
                    align-items: center;
                    gap: var(--joy-core-spacing-1);
                    background-color: var(--joy-color-neutral-0);
                    border-radius: 30px;
                    padding: var(--joy-core-spacing-1) var(--joy-core-spacing-2);
                    color: var(--joy-color-neutral-70);
                }
            }

            &__reinsurance-stars {
                display: inline-flex;
                align-items: center;
                gap: var(--joy-core-spacing-0);
            }
        }
    }
</style>
